class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this.hausielGeneration = 0;
    this.hausielFrame = null;
    this.hausielTimer = null;
    this.addEventListener('keyup', (e) => e.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay')?.addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    this.ensureHausielStyles();
    this.hausielUnsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, () => this.scheduleHausielUpdate());
    // HAUSIEL: Dawn replaces cart-drawer-items and the footer after product-form adds
    // (CartItems.onCartUpdate). That wipes the progress bars and footer notes, so restore them.
    this.hausielObserver = new MutationObserver(() => this.restoreHausielBlocks());
    this.hausielObserver.observe(this, { childList: true, subtree: true });
  }

  restoreHausielBlocks() {
    if (this.hausielRestoreQueued || this.classList.contains('is-empty')) return;
    const wrapper = this.querySelector('#CartDrawer-CartItems .drawer__cart-items-wrapper');
    const footer = this.querySelector('.drawer__footer');
    const needsFooter = Boolean(footer?.querySelector('#CartDrawer-Checkout') && !footer.querySelector('.hausiel-drawer-assurance'));
    const needsMerch = Boolean(wrapper && !this.querySelector('[data-hausiel-drawer-merch-slot]'));
    if (!needsFooter && !needsMerch) return;
    this.hausielRestoreQueued = true;
    setTimeout(() => {
      this.hausielRestoreQueued = false;
      this.ensureHausielEnhancements();
      if (needsMerch) this.scheduleHausielUpdate();
    }, 0);
  }

  disconnectedCallback() {
    this.hausielObserver?.disconnect();
    this.hausielUnsubscribe?.();
    clearTimeout(this.hausielTimer);
    if (this.hausielFrame) cancelAnimationFrame(this.hausielFrame);
  }

  setHeaderCartIconAccessibility() {
    const link = document.querySelector('#cart-icon-bubble');
    if (!link) return;
    link.setAttribute('role', 'button');
    link.setAttribute('aria-haspopup', 'dialog');
    link.addEventListener('click', (e) => { e.preventDefault(); this.open(link); });
    link.addEventListener('keydown', (e) => {
      if (e.code.toUpperCase() === 'SPACE') { e.preventDefault(); this.open(link); }
    });
  }

  open(triggeredBy) {
    if (this.classList.contains('active')) {
      this.ensureHausielEnhancements();
      this.scheduleHausielUpdate();
      return;
    }
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const note = this.querySelector('[id^="Details-"] summary');
    if (note && !note.hasAttribute('role')) this.setSummaryAccessibility(note);
    setTimeout(() => this.classList.add('animate', 'active'));
    this.addEventListener('transitionend', () => {
      const container = this.classList.contains('is-empty') ? this.querySelector('.drawer__inner-empty') : document.getElementById('CartDrawer');
      const focus = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
      if (container && focus) trapFocus(container, focus);
    }, { once: true });
    document.body.classList.add('overflow-hidden');
    this.querySelector('cart-drawer-items')?.dispatchViewEvent();
    this.ensureHausielEnhancements();
    this.scheduleHausielUpdate();
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(note) {
    note.setAttribute('role', 'button');
    note.setAttribute('aria-expanded', 'false');
    if (note.nextElementSibling?.id) note.setAttribute('aria-controls', note.nextElementSibling.id);
    note.addEventListener('click', (e) => e.currentTarget.setAttribute('aria-expanded', !e.currentTarget.closest('details').hasAttribute('open')));
    note.parentElement?.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(state) {
    this.classList.remove('is-empty');
    this.productId = state.id;
    this.getSectionsToRender().forEach((section) => {
      const el = section.selector ? document.querySelector(section.selector) : document.getElementById(section.id);
      if (el && state.sections?.[section.id]) el.innerHTML = this.getSectionInnerHTML(state.sections[section.id], section.selector);
    });
    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay')?.addEventListener('click', this.close.bind(this));
      this.ensureHausielEnhancements();
      this.open();
      this.scheduleHausielUpdate();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector)?.innerHTML || '';
  }
  getSectionsToRender() { return [{ id: 'cart-drawer', selector: '#CartDrawer' }, { id: 'cart-icon-bubble' }]; }
  getSectionDOM(html, selector = '.shopify-section') { return new DOMParser().parseFromString(html, 'text/html').querySelector(selector); }
  setActiveElement(el) { this.activeElement = el; }

  scheduleHausielUpdate(attempt = 0) {
    clearTimeout(this.hausielTimer);
    if (this.hausielFrame) cancelAnimationFrame(this.hausielFrame);
    this.hausielFrame = requestAnimationFrame(() => {
      this.hausielFrame = null;
      const wrapper = this.querySelector('#CartDrawer-CartItems .drawer__cart-items-wrapper');
      if (wrapper) return this.updateHausielMerch();
      if (attempt < 3 && !this.classList.contains('is-empty')) {
        this.hausielTimer = setTimeout(() => this.scheduleHausielUpdate(attempt + 1), 60);
      }
    });
  }

  money(cents) {
    return new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY', minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(Number(cents || 0) / 100);
  }
  esc(v) { return String(v || '').replace(/[&<>'"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;' }[c])); }
  tags(p) { return Array.isArray(p?.tags) ? p.tags.map((x) => String(x).trim()) : String(p?.tags || '').split(',').map((x) => x.trim()).filter(Boolean); }
  isAddon(p) { return this.tags(p).includes('SEPET_ARTIRICI'); }
  isMain(p) { return this.tags(p).includes('UPSELL_ANA'); }
  isMultibuy(p) { return Array.isArray(window.HausielUpsell?.multibuyHandles) && window.HausielUpsell.multibuyHandles.includes(String(p?.handle || '')); }
  relations(id) { const r = window.HausielUpsell?.relations?.[String(id)]; return Array.isArray(r) ? r.map(String) : []; }

  ensureHausielEnhancements() {
    const empty = this.querySelector('.cart-drawer__empty-content');
    if (empty) {
      const title = empty.querySelector('.cart__empty-text');
      if (title) title.textContent = 'Sepetiniz henüz boş.';
      const button = empty.querySelector('a.button');
      if (button) { button.textContent = 'Tüm ürünleri incele'; button.href = '/collections/ev-yasam'; }
      if (!empty.querySelector('.hausiel-empty-drawer-links')) {
        const nav = document.createElement('nav');
        nav.className = 'hausiel-empty-drawer-links';
        nav.setAttribute('aria-label', 'Ürün kategorileri');
        nav.innerHTML = '<a href="/collections/mutfak">Mutfak</a><a href="/collections/banyo">Banyo</a><a href="/collections/duzenleme-saklama">Düzenleme</a>';
        (button || title)?.insertAdjacentElement('afterend', nav);
      }
    }
    const footer = this.querySelector('.drawer__footer');
    const ctas = footer?.querySelector('#CartDrawer-Checkout')?.closest('.cart__ctas');
    if (ctas && !footer.querySelector('.hausiel-drawer-assurance')) {
      const p = document.createElement('p');
      p.className = 'hausiel-drawer-assurance';
      p.textContent = '14 gün cayma hakkı · 649 TL+ ücretsiz kargo';
      ctas.insertAdjacentElement('afterend', p);
    }
  }

  merchSlot() {
    const contents = this.querySelector('#CartDrawer-CartItems');
    const items = contents?.querySelector('.drawer__cart-items-wrapper');
    if (!contents || !items) return null;
    let slot = contents.querySelector('[data-hausiel-drawer-merch-slot]');
    if (!slot) {
      slot = document.createElement('div');
      slot.className = 'hausiel-drawer-merch-slot';
      slot.setAttribute('data-hausiel-drawer-merch-slot', '');
      items.insertAdjacentElement('afterend', slot);
    }
    return slot;
  }

  ensureHausielStyles() {
    if (document.getElementById('HausielCartProgressStyles')) return;
    const s = document.createElement('style');
    s.id = 'HausielCartProgressStyles';
    s.textContent = `.hausiel-cart-progress{margin:0 0 1.2rem;padding:1.1rem;border:1px solid rgba(var(--color-foreground),.1);border-radius:1rem;background:rgba(var(--color-foreground),.025)}.hausiel-cart-progress__row+.hausiel-cart-progress__row{margin-top:.9rem;padding-top:.9rem;border-top:1px solid rgba(var(--color-foreground),.08)}.hausiel-cart-progress__label{display:flex;justify-content:space-between;gap:1rem;align-items:baseline;line-height:1.35}.hausiel-cart-progress__label strong{font-size:1.05rem}.hausiel-cart-progress__label span{font-size:.95rem;opacity:.64;text-align:right}.hausiel-cart-progress__track{height:.35rem;margin-top:.55rem;overflow:hidden;background:rgba(var(--color-foreground),.1)}.hausiel-cart-progress__fill{display:block;height:100%;background:rgb(var(--color-foreground));transition:width .25s ease}.hausiel-drawer-merch-slot{display:block;width:100%;padding:1rem 0 .25rem}.hausiel-drawer-upsell{margin:0 0 1.2rem;padding:1rem;border:1px solid rgba(var(--color-foreground),.14);border-radius:1rem;background:rgba(var(--color-foreground),.018)}.hausiel-drawer-upsell__eyebrow{display:flex;justify-content:space-between;gap:.8rem;margin-bottom:.35rem;font-size:.95rem;font-weight:700;letter-spacing:.06em}.hausiel-drawer-upsell__choice-note{display:block;margin:0 0 .8rem;font-size:.9rem;line-height:1.35;opacity:.64}.hausiel-drawer-upsell__cards{display:grid;gap:.8rem}.hausiel-drawer-upsell__card{display:grid;grid-template-columns:6.5rem minmax(0,1fr);gap:.9rem;align-items:center;padding:.75rem;border:1px solid rgba(var(--color-foreground),.09);border-radius:.8rem}.hausiel-drawer-upsell__image{width:6.5rem;height:6.5rem;object-fit:contain;border-radius:.6rem;background:rgba(var(--color-foreground),.025)}.hausiel-drawer-upsell__title{display:block;color:inherit;text-decoration:none;font-size:1.02rem;font-weight:700;line-height:1.3}.hausiel-drawer-upsell__prices{display:flex;gap:.55rem;align-items:baseline;margin:.3rem 0}.hausiel-drawer-upsell__prices s{font-size:.9rem;opacity:.5}.hausiel-drawer-upsell__prices strong{font-size:1.1rem}.hausiel-drawer-upsell__saving{font-size:.88rem;font-weight:700}.hausiel-drawer-upsell__note{display:block;margin-top:.18rem;font-size:.86rem;line-height:1.3;opacity:.62}.hausiel-drawer-upsell__button{width:100%;min-height:3.6rem;margin-top:.55rem;padding:0 .8rem}.hausiel-drawer-upsell__error{margin:.45rem 0 0;font-size:.9rem;line-height:1.3}.hausiel-drawer-assurance{margin:.75rem 0 0;text-align:center;font-size:1rem;line-height:1.4;opacity:.62}.hausiel-empty-drawer-links{display:flex;flex-wrap:wrap;justify-content:center;gap:.6rem;margin-top:1rem}.hausiel-empty-drawer-links a{padding:.55rem .85rem;border:1px solid rgba(var(--color-foreground),.14);border-radius:999px;color:inherit;text-decoration:none;font-size:1rem}@media(max-width:749px){.hausiel-drawer-merch-slot{padding-top:.75rem}.hausiel-drawer-assurance{font-size:.95rem}}`;
    document.head.appendChild(s);
  }

  loadProducts() {
    if (!this.hausielProductsPromise) {
      this.hausielProductsPromise = fetch('/collections/ev-yasam/products.json?limit=50', { headers: { Accept: 'application/json' } })
        .then((r) => r.ok ? r.json() : Promise.reject(new Error('Products unavailable')))
        .then((x) => Array.isArray(x?.products) ? x.products : []);
    }
    return this.hausielProductsPromise;
  }

  preferences(products) {
    const bundles = [], categories = [], add = (a, v) => v && !a.includes(v) && a.push(v);
    products.forEach((p) => {
      const t = this.tags(p);
      ['BUNDLE_DUS', 'BUNDLE_MUTFAK', 'BUNDLE_BANYO', 'BUNDLE_DUZEN'].forEach((x) => t.includes(x) && add(bundles, x));
      if (t.includes('MUTFAK')) { add(bundles, 'BUNDLE_MUTFAK'); add(categories, 'MUTFAK'); }
      if (t.includes('BANYO')) { add(bundles, 'BUNDLE_BANYO'); add(categories, 'BANYO'); }
      if (t.includes('DUZENLEME')) { add(bundles, 'BUNDLE_DUZEN'); add(categories, 'DUZENLEME'); }
    });
    return { bundles, categories };
  }

  rankCandidates(cartProducts, products, cartIds) {
    const pref = this.preferences(cartProducts), direct = [];
    cartProducts.forEach((p) => this.relations(p.id).forEach((id) => !direct.includes(id) && direct.push(id)));
    const rank = (p) => {
      const t = this.tags(p), di = direct.indexOf(String(p.id));
      if (di >= 0) return [0, di];
      const bi = pref.bundles.findIndex((x) => t.includes(x)); if (bi >= 0) return [1, bi];
      const ci = pref.categories.findIndex((x) => t.includes(x)); if (ci >= 0) return [2, ci];
      return [3, 999];
    };
    return products.filter((p) => {
      if (!this.isAddon(p) || cartIds.has(String(p.id))) return false;
      const v = p.variants?.find((x) => x.available !== false) || p.variants?.[0];
      return Boolean(v && parseFloat(v.price || '0') > 0);
    }).sort((a, b) => { const x = rank(a), y = rank(b); return x[0] - y[0] || x[1] - y[1] || String(a.title).localeCompare(String(b.title), 'tr'); }).slice(0, 4);
  }

  renderUpsell(cart, qualifyingSubtotal, slot, products, generation, addonInCart) {
    if (!slot?.isConnected || generation !== this.hausielGeneration) return;
    const map = new Map(products.map((p) => [String(p.id), p]));
    const cartIds = new Set(cart.items.map((i) => String(i.product_id)));
    const mains = cart.items.map((i) => map.get(String(i.product_id))).filter((p) => p && this.isMain(p));
    const candidates = this.rankCandidates(mains, products, cartIds)
      .filter((p) => !cartIds.has(String(p.id)))
      .slice(0, 4);
    let panel = slot.querySelector('[data-hausiel-drawer-upsell]');
    if (!candidates.length) { panel?.remove(); return; }

    const unlocked = qualifyingSubtotal >= 150000 && !addonInCart;
    const used = qualifyingSubtotal >= 150000 && addonInCart;
    if (!panel) {
      panel = document.createElement('div');
      panel.className = 'hausiel-drawer-upsell';
      panel.setAttribute('data-hausiel-drawer-upsell', '');
      slot.appendChild(panel);
    }

    const cards = candidates.map((p) => {
      const v = p.variants?.find((x) => x.available !== false) || p.variants?.[0];
      const raw = p.images?.[0] || p.image, image = typeof raw === 'string' ? raw : raw?.src || '';
      const price = Math.round(parseFloat(v.price || '0') * 100), offer = Math.round(price * .8), saving = price - offer;
      const title = this.esc(p.title), handle = this.esc(p.handle), one = p.variants?.length === 1;
      const priceHtml = unlocked ? `<s>${this.money(price)}</s><strong>${this.money(offer)}</strong>` : `<strong>${this.money(price)}</strong>`;
      const extra = unlocked ? `<span class="hausiel-drawer-upsell__saving">${this.money(saving)} avantaj</span><small class="hausiel-drawer-upsell__note">Sepete özel teklif bu ürünün 1 adedine uygulanır.</small>` : `<small class="hausiel-drawer-upsell__note">${used ? 'Sepete özel teklifiniz sepetteki uygun tamamlayıcıda kullanılıyor.' : '1.500 TL ana ürün toplamında %20 teklif açılır.'}</small>`;
      const cta = unlocked ? 'Sepete %20 indirimli ekle' : 'Sepete ekle';
      return `<article class="hausiel-drawer-upsell__card" data-hausiel-drawer-upsell-card>${image ? `<img class="hausiel-drawer-upsell__image" src="${this.esc(image)}" alt="${title}" loading="lazy">` : '<div></div>'}<div><a class="hausiel-drawer-upsell__title" href="/products/${handle}">${title}</a><div class="hausiel-drawer-upsell__prices">${priceHtml}</div>${extra}${one ? `<button type="button" class="button button--secondary hausiel-drawer-upsell__button" data-hausiel-drawer-upsell-add data-variant-id="${String(v.id)}">${cta}</button>` : `<a class="button button--secondary hausiel-drawer-upsell__button" href="/products/${handle}">Seçenekleri gör</a>`}<p class="hausiel-drawer-upsell__error" role="alert" aria-live="polite" data-hausiel-drawer-upsell-error hidden></p></div></article>`;
    }).join('');

    const left = unlocked ? 'SEPETE ÖZEL TEKLİF' : 'TAMAMLAYICI ÖNERİLER';
    const right = unlocked ? '-%20' : used ? 'TEKLİF KULLANILDI' : '1.500 TL’DE -%20';
    const note = unlocked ? 'Aşağıdaki seçeneklerden eklediğiniz 1 uygun tamamlayıcının 1 adedine %20 otomatik indirim uygulanır.' : used ? 'Sepete özel %20 teklifiniz sepetteki uygun tamamlayıcı ürünün 1 adedine uygulanır.' : 'Ana ürün toplamı 1.500 TL olduğunda seçtiğiniz 1 uygun tamamlayıcının 1 adedine %20 otomatik indirim uygulanır.';
    if (generation !== this.hausielGeneration || !panel.isConnected) return;
    panel.innerHTML = `<div class="hausiel-drawer-upsell__eyebrow"><span>${left}</span><span>${right}</span></div><small class="hausiel-drawer-upsell__choice-note">${note}</small><div class="hausiel-drawer-upsell__cards">${cards}</div>`;

    panel.querySelectorAll('[data-hausiel-drawer-upsell-add]').forEach((button) => button.addEventListener('click', async () => {
      if (button.disabled) return;
      const errorBox = button.closest('[data-hausiel-drawer-upsell-card]')?.querySelector('[data-hausiel-drawer-upsell-error]');
      if (errorBox) { errorBox.hidden = true; errorBox.textContent = ''; }
      const old = button.textContent; button.disabled = true; button.textContent = 'Ekleniyor…';
      try {
        const payload = { items: [{ id: String(button.dataset.variantId), quantity: 1 }], sections: this.getSectionsToRender().map((s) => s.id), sections_url: location.pathname };
        const r = await fetch(routes.cart_add_url, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(payload) });
        const data = await r.json();
        if (!r.ok || data.status) throw new Error(data.description || data.message || 'Ürün sepete eklenemedi.');
        if (data.sections) this.renderContents(data); else location.href = routes.cart_url;
      } catch (e) {
        if (errorBox) { errorBox.textContent = e.message || 'Ürün sepete eklenemedi.'; errorBox.hidden = false; }
        button.disabled = false; button.textContent = 'Tekrar deneyin'; setTimeout(() => button.textContent = old, 1600);
      }
    }));
  }

  updateHausielMerch() {
    this.ensureHausielEnhancements();
    const slot = this.merchSlot();
    if (!slot) return;
    const generation = ++this.hausielGeneration;
    Promise.all([
      fetch(`${routes.cart_url}.json`, { headers: { Accept: 'application/json' } }).then((r) => r.ok ? r.json() : Promise.reject()),
      this.loadProducts(),
    ]).then(([cart, products]) => {
      if (generation !== this.hausielGeneration || !slot.isConnected) return;
      let progress = slot.querySelector('[data-hausiel-cart-progress]');
      if (!cart?.item_count) { progress?.remove(); slot.querySelector('[data-hausiel-drawer-upsell]')?.remove(); return; }
      if (!progress) { progress = document.createElement('div'); progress.className = 'hausiel-cart-progress'; progress.setAttribute('data-hausiel-cart-progress', ''); slot.prepend(progress); }

      const map = new Map(products.map((p) => [String(p.id), p])), qty = new Map();
      let qualifying = 0, addonInCart = false;
      cart.items.forEach((i) => {
        const id = String(i.product_id), p = map.get(id); if (!p) return;
        if (this.isAddon(p)) addonInCart = true;
        if (this.isMultibuy(p)) qty.set(id, (qty.get(id) || 0) + Number(i.quantity || 0));
        if (this.isMain(p)) qualifying += Number(i.final_line_price || i.original_line_price || 0);
      });
      const q = [...qty.values()], active = q.some((x) => x >= 2), near = q.some((x) => x === 1);
      const multiTitle = active ? 'Çoklu alım avantajı aktif' : 'Çoklu alım';
      const multiDetail = active ? 'Adet indirimi sepette otomatik uygulandı' : near ? '1 adet daha ekleyin → çoklu alım avantajı başlar' : 'Uygun ürünlerde 2–10 adet çoklu alım avantajı';
      const multiPct = active ? 100 : near ? 50 : 0, threshold = 150000, shipThreshold = 64900;
      const shippingTotal = Number(cart.total_price || 0);
      const shipLeft = Math.max(0, shipThreshold - shippingTotal), shipPct = Math.min(100, shippingTotal / shipThreshold * 100);
      const shipTitle = shipLeft ? `Ücretsiz kargoya ${this.money(shipLeft)} kaldı` : 'Ücretsiz kargo aktif';
      const shipDetail = shipLeft ? '649 TL ve üzeri siparişlerde ücretsiz kargo' : '649 TL eşiğine ulaştınız';
      const offerLeft = Math.max(0, threshold - qualifying), offerPct = Math.min(100, qualifying / threshold * 100);
      const offerTitle = offerLeft ? `Sepete özel %20'ye ${this.money(offerLeft)} kaldı` : addonInCart ? 'Sepete özel %20 aktif' : 'Sepete özel %20 açıldı';
      const offerDetail = offerLeft ? 'Ana ürün toplamı 1.500 TL olduğunda açılır' : addonInCart ? 'Uygun tamamlayıcı ürünün 1 adedine uygulanır' : 'Aşağıdaki 4 tamamlayıcıdan birini seçin';
      const row = (a, b, p) => `<div class="hausiel-cart-progress__row"><div class="hausiel-cart-progress__label"><strong>${a}</strong><span>${b}</span></div><div class="hausiel-cart-progress__track" aria-hidden="true"><span class="hausiel-cart-progress__fill" style="width:${p}%"></span></div></div>`;
      progress.innerHTML = row(multiTitle, multiDetail, multiPct) + row(shipTitle, shipDetail, shipPct) + row(offerTitle, offerDetail, offerPct);
      this.renderUpsell(cart, qualifying, slot, products, generation, addonInCart);
    }).catch(() => {
      if (generation !== this.hausielGeneration || !slot.isConnected) return;
      slot.querySelector('[data-hausiel-cart-progress]')?.remove();
      slot.querySelector('[data-hausiel-drawer-upsell]')?.remove();
    });
  }
}
customElements.define('cart-drawer', CartDrawer);
class CartDrawerItems extends CartItems {
  getSectionsToRender() { return [{ id: 'CartDrawer', section: 'cart-drawer', selector: '.drawer__inner' }, { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' }]; }
}
if (!customElements.get('cart-drawer-items')) customElements.define('cart-drawer-items', CartDrawerItems);
